import { useCallback } from "react";

import type { Credentials } from "react-native-auth0";

import type { LogoutReason } from "@/constants/Login";
import useAuth from "@/hooks/useAuth";
import type { SerializableAuthResponse } from "@/utils/auth.util";

type BiometricAuthFunction = ({
  isAppInitialization,
}: {
  isAppInitialization: boolean;
}) => Promise<SerializableAuthResponse<Credentials | void> | undefined>;

type OnBiometricAuthFailureCallback = ({
  logoutReason,
  shouldDisableBiometricAuth,
  toastText,
}: {
  logoutReason: LogoutReason;
  shouldDisableBiometricAuth: boolean;
  toastText?: string;
}) => Promise<void>;

const useBiometricAuth = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  isFirstLaunchSetupDone: _isFirstLaunchSetupDone,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  isRootNavigatorReady: _isRootNavigatorReady,
  onAuthFullyInitialized: _onAuthFullyInitialized,
}: {
  isFirstLaunchSetupDone: boolean;
  isRootNavigatorReady: boolean;
  onAuthFullyInitialized: () => void;
}) => {
  const { logout } = useAuth();

  const doBiometricAuth: BiometricAuthFunction = useCallback(
    async () => undefined,
    [],
  );

  const onBiometricAuthFailure: OnBiometricAuthFailureCallback = async ({
    logoutReason,
  }: {
    logoutReason: LogoutReason;
  }) => {
    // "log out" will happen as follows:
    //  1) redirecting to the login page
    //  2) this unmounts all currently rendered components and associated RTKQuery queries
    //  3) the login page has a useEffect to invoke hooks/useOnlyFromLoginPageLogout, to do a full reset/proper logout, which sets isAuthenticated === false
    await logout(logoutReason);
  };

  return {
    doBiometricAuth,
    onBiometricAuthFailure,
  };
};

export type { BiometricAuthFunction, OnBiometricAuthFailureCallback };

export default useBiometricAuth;
